import { useState } from "react";
import { PageLoader, useMinLoading } from "@/components/Preloader";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PenLine, UserPlus, UserCheck, Globe, BadgeCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { timeAgo } from "@/lib/api";
import { Article, CATEGORY_ROUTES } from "@/lib/types";
import { ArticleCard } from "@/components/ArticleCard";
import { AuroraAvatar } from "@/components/AuthorAvatar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";

const XIcon = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const catName = (slug: string) =>
  CATEGORY_ROUTES.find((c) => c.slug === slug)?.name ||
  slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
const catPath = (slug: string) => CATEGORY_ROUTES.find((c) => c.slug === slug)?.path;

export const fmtReads = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
    : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K` : String(n);

const memberSince = (iso?: string) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" }); }
  catch { return null; }
};

const CHIP_ACCENTS = ["#7C3AED", "#00E5FF", "#34d399", "#60a5fa", "#a855f7", "#10D7F2"];

const Stat = ({ label, value, testid }: { label: string; value: string | number; testid: string }) => (
  <div data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">{label}</p>
    <p className="font-heading font-bold text-white text-lg mt-0.5">{value}</p>
  </div>
);

const ExpertiseChips = ({ items }: { items: string[] }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, 6);
  const hidden = items.length - 6;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-4" data-testid="author-expertise">
      {shown.map((x, i) => {
        const c = CHIP_ACCENTS[i % CHIP_ACCENTS.length];
        return (
          <Link key={x} to={`/search?q=${encodeURIComponent(x)}`}
            className="rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide transition-colors hover:brightness-125"
            style={{ borderColor: `${c}4d`, color: c, backgroundColor: `${c}14` }}>
            {x}
          </Link>
        );
      })}
      {!expanded && hidden > 0 && (
        <button onClick={() => setExpanded(true)} data-testid="author-expertise-more"
          className="rounded-full border border-white/15 bg-white/[0.04] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-gray-400 hover:text-white hover:border-white/40 transition-colors">
          +{hidden} more
        </button>
      )}
    </div>
  );
};

export default function AuthorPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["author", slug],
    queryFn: async () => (await api.get(`/authors/${slug}`)).data,
  });

  const showLoader = useMinLoading(isLoading, 700, slug);
  if (showLoader) return <PageLoader />;
  if (isError || !data?.author) {
    return <div className="max-w-4xl mx-auto px-4 py-24 text-center">
      <h1 className="font-heading text-2xl text-white mb-3">Author not found</h1>
      <Link to="/" className="text-[#00E5FF] hover:underline">Back to homepage</Link>
    </div>;
  }

  const author = data.author;
  const following = !!user?.followed_authors?.includes(slug || "");
  const since = memberSince(author.joined);
  const beat: string[] = (author.top_categories || []).slice(0, 3);

  const toggleFollow = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      if (following) {
        await api.delete("/account/follow", { data: { follow_type: "author", slug } });
        toast(`Unfollowed ${author.name}`);
      } else {
        await api.post("/account/follow", { follow_type: "author", slug });
        toast.success(`Following ${author.name} — new stories will appear in My Feed`);
      }
      await refresh();
    } catch { toast.error("Could not update follow"); }
  };

  const followBtn = (
    <Button onClick={toggleFollow} data-testid="follow-author-button"
      className={`rounded-md px-6 font-bold transition-colors ${
        following
          ? "bg-transparent border border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10"
          : "bg-[#7C3AED] text-white hover:bg-[#6a2dd0]"}`}>
      {following ? <><UserCheck className="h-4 w-4 mr-1.5" /> Following</> : <><UserPlus className="h-4 w-4 mr-1.5" /> Follow</>}
    </Button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="author-page">
      <Seo title={`${author.name} — Author`}
        description={author.bio || `Stories by ${author.name} on NewsLayer — crypto, Web3 and policy coverage.`}
        path={`/author/${slug}`} />

      <header className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#050506] px-5 py-8 sm:px-8 sm:py-10 mb-10"
        data-testid="author-masthead">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 h-64 w-64 rounded-full bg-[#7C3AED]/[0.12] blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-28 -right-16 h-64 w-64 rounded-full bg-[#00E5FF]/[0.07] blur-3xl" />

        <div className="relative flex flex-wrap items-start gap-6">
          <AuroraAvatar name={author.name} avatarUrl={author.avatar_url} seed={author.handle || author.slug} />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-xs uppercase tracking-widest text-[#00E5FF] mb-1.5 flex items-center gap-1.5">
              <PenLine className="h-3 w-3" /> Author
            </p>
            <h1 className="font-heading font-black text-white text-2xl sm:text-4xl flex items-center gap-2.5 flex-wrap" data-testid="author-name">
              {author.name}
              {author.is_program_author && (
                <span data-testid="author-verified-seal"
                  title="NewsLayer Approved Author — vetted by our editorial team"
                  className="inline-flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/10">
                  <BadgeCheck className="h-4 w-4 sm:h-[18px] sm:w-[18px] text-[#00E5FF]" />
                </span>
              )}
            </h1>
            <p className="font-mono text-xs text-gray-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              {author.handle && <span className="text-[#00E5FF]" data-testid="author-handle">@{author.handle}</span>}
              {author.joined && <span>Joined {timeAgo(author.joined)}</span>}
            </p>
            {beat.length > 0 && (
              <p className="text-sm text-gray-400 mt-2" data-testid="author-beat-line">
                Covers{" "}
                {beat.map((c, i) => (
                  <span key={c}>
                    {catPath(c)
                      ? <Link to={catPath(c)!} className="text-gray-200 hover:text-[#00E5FF] transition-colors font-medium">{catName(c)}</Link>
                      : <span className="text-gray-200 font-medium">{catName(c)}</span>}
                    {i < beat.length - 1 && <span className="text-gray-600"> · </span>}
                  </span>
                ))}
              </p>
            )}
            {author.bio && (
              <p className="text-gray-300 text-sm leading-relaxed mt-3 max-w-2xl" data-testid="author-bio">
                {author.bio}
              </p>
            )}
            {(author.expertise || []).length > 0 && <ExpertiseChips items={author.expertise} />}
            {(author.socials?.x || author.socials?.website) && (
              <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="author-socials">
                {author.socials?.x && (
                  <a href={author.socials.x} target="_blank" rel="noopener noreferrer" aria-label="X profile"
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-white hover:border-white/40 transition-colors">
                    <XIcon className="h-3.5 w-3.5" /> X / Twitter
                  </a>
                )}
                {author.socials?.website && (
                  <a href={author.socials.website} target="_blank" rel="noopener noreferrer" aria-label="Website"
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-white hover:border-white/40 transition-colors">
                    <Globe className="h-3.5 w-3.5" /> Website
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="sm:ml-auto">{followBtn}</div>
        </div>

        <div className="relative mt-7 pt-5 border-t border-white/[0.07] flex flex-wrap items-end gap-x-10 gap-y-4" data-testid="author-stat-rail">
          {author.article_count > 0 ? (
            <Stat label="Stories" value={author.article_count} testid="author-stat-stories" />
          ) : (
            <div data-testid="author-new-voice">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">Status</p>
              <p className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[#c4a6ff]">
                <Sparkles className="h-3 w-3" /> New voice
              </p>
            </div>
          )}
          {author.total_reads > 0 && <Stat label="Total reads" value={fmtReads(author.total_reads)} testid="author-stat-reads" />}
          {author.followers > 0 && <Stat label="Followers" value={author.followers} testid="author-stat-followers" />}
          {since && <Stat label="Member since" value={since} testid="author-stat-since" />}
        </div>
      </header>

      {data.articles.length === 0 ? (
        <div className="relative overflow-hidden rounded-2xl border border-[#7C3AED]/25 bg-[#050506] px-6 py-14 text-center" data-testid="author-no-stories">
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.3]"
            style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
          <div aria-hidden className="pointer-events-none absolute -top-20 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-[#7C3AED]/10 blur-3xl" />
          <div className="relative">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#7C3AED]/40 bg-[#7C3AED]/15">
              <PenLine className="h-5 w-5 text-[#c4a6ff]" />
            </span>
            <p className="font-heading font-bold text-white text-lg mt-4">First story in the works</p>
            <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto leading-relaxed">
              {author.name} joined the NewsLayer author program{author.joined ? ` ${timeAgo(author.joined)}` : ""}.
              Follow now and be the first to read their debut story.
            </p>
            {!following && (
              <Button onClick={toggleFollow} data-testid="author-empty-follow-button"
                className="mt-5 rounded-md bg-[#7C3AED] px-6 font-bold text-white hover:bg-[#6a2dd0]">
                <UserPlus className="h-4 w-4 mr-1.5" /> Follow {author.name.split(" ")[0]}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.articles.map((a: Article) => <ArticleCard key={a.id} article={a} />)}
        </div>
      )}
    </div>
  );
}
